/* Defer the trusted, official Klarna app block until both purposes are explicitly consented.
 * The vintage/global Klarna ScriptTag must be disabled separately. This does not
 * alter offers, payment selection, cart data, privacy choices or SDK internals.
 */
(() => {
  'use strict';
  if (window.mmKlarnaConsentInitialized) return;
  window.mmKlarnaConsentInitialized = true;

  const selector = '[data-mm-klarna-presentation="prominent-v1"]';
  const hosts = new Map();
  const initializedHosts = new WeakMap();
  let scheduled = false, blocked = false, activated = false, deniedLatch = false;
  let featureRequestedAt = null;

  const allowed = () => {
    try {
      const api = window.Shopify?.customerPrivacy;
      const marketing = api?.marketingAllowed?.();
      const analytics = api?.analyticsProcessingAllowed?.();
      const explicit = api?.currentVisitorConsent?.();
      // A denial event can precede the API update. Do not trust stale true values.
      if (marketing === false || analytics === false || explicit?.marketing === 'no' || explicit?.analytics === 'no') deniedLatch = false;
      return marketing === true && analytics === true
        && explicit?.marketing === 'yes' && explicit?.analytics === 'yes'
        && !deniedLatch && !blocked;
    } catch (_) { return false; }
  };
  const mark = (host, status) => {
    if (host.dataset.mmKlarnaConsent !== status) host.dataset.mmKlarnaConsent = status;
  };
  const fallbackFor = host => host.querySelector('[data-mm-klarna-fallback]');
  const showFallback = host => {
    const fallback = fallbackFor(host);
    if (fallback) fallback.hidden = false;
  };

  const revoke = () => {
    document.querySelectorAll(selector).forEach(host => {
      showFallback(host);
      mark(host, 'consent-required');
      if (activated) host.hidden = true;
    });
    if (!activated || blocked) return;
    // Removing a placement cannot stop a loaded third-party runtime. A new
    // document is required; do not edit consent cookies or empty the cart.
    blocked = true;
    observer.disconnect();
    window.clearInterval(privacyTimer);
    hosts.forEach(entry => entry.live.remove());
    window.location.reload();
  };

  const requestPrivacy = () => {
    const shopify = window.Shopify;
    if (shopify?.customerPrivacy || !shopify?.loadFeatures) return;
    if (featureRequestedAt !== null && Date.now() - featureRequestedAt < 30000) return;
    featureRequestedAt = Date.now();
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        if (!error) schedule();
      });
    } catch (_) { /* Missing privacy API leaves the neutral fallback available. */ }
  };

  const updatePresentation = (host, entry) => {
    if (!allowed() || !host.isConnected) return;
    if (!host.contains(entry.live)) {
      showFallback(host);
      mark(host, 'unavailable');
      return;
    }
    // Presence only: never copy the rate/terms into our own financing claims.
    // Activation is not proof of an offer: keep fallback through SDK/network errors.
    const populated = Array.from(entry.live.querySelectorAll('klarna-placement')).some(placement => {
      const message = placement.shadowRoot?.querySelector('[part~="osm-message"]');
      return Boolean(message?.textContent?.trim());
    });
    const fallback = fallbackFor(host);
    if (fallback) fallback.hidden = populated;
    mark(host, populated ? 'ready' : 'loading');
  };

  const activate = host => {
    const existing = initializedHosts.get(host);
    if (existing) {
      hosts.set(host, existing);
      updatePresentation(host, existing);
      return;
    }
    const template = host.querySelector('template[data-mm-klarna-template]');
    if (!template?.content || template.closest(selector) !== host || !allowed()) return;
    const fragment = template.content.cloneNode(true);
    const scripts = Array.from(fragment.querySelectorAll('script')).map(original => {
      const placeholder = document.createComment('Official Klarna app initialization');
      original.replaceWith(placeholder);
      return { original, placeholder };
    });
    const live = document.createElement('div');
    live.dataset.mmKlarnaLive = '';
    const entry = { live };
    // Claim the host before any synchronous official script or DOM observer runs.
    hosts.set(host, entry);
    initializedHosts.set(host, entry);
    activated = true;
    mark(host, 'loading');
    live.appendChild(fragment);
    host.appendChild(live);
    for (const { original, placeholder } of scripts) {
      if (!allowed() || !host.isConnected || blocked) { revoke(); return; }
      const script = document.createElement('script');
      for (const attribute of original.attributes) script.setAttribute(attribute.name, attribute.value);
      // Browsers may hide CSP nonce values from getAttribute/attributes.
      if (original.nonce) script.nonce = original.nonce;
      script.textContent = original.textContent;
      placeholder.replaceWith(script);
    }
    updatePresentation(host, entry);
  };

  function reconcile() {
    scheduled = false;
    if (blocked) return;
    requestPrivacy();
    if (!allowed()) { revoke(); return; }
    for (const [host, entry] of hosts) {
      if (!host.isConnected) hosts.delete(host);
      else updatePresentation(host, entry);
    }
    document.querySelectorAll(selector).forEach(host => {
      if (!hosts.has(host)) activate(host);
    });
  }
  function schedule() {
    if (scheduled || blocked) return;
    scheduled = true;
    window.setTimeout(reconcile, 0);
  }

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, { subtree: true, childList: true });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('visitorConsentCollected', event => {
    const detail = event.detail || {};
    if (detail.marketingAllowed === false || detail.analyticsAllowed === false) {
      deniedLatch = true;
      revoke();
    } else if (detail.marketingAllowed === true && detail.analyticsAllowed === true) {
      deniedLatch = false;
    }
    schedule();
  });
  window.addEventListener('pageshow', schedule);
  // Also catches late APIs, quiet consent changes and asynchronously filled shadow DOM.
  const privacyTimer = window.setInterval(schedule, 1000);
  reconcile();
})();
